import React, { useState, useEffect } from 'react';
import { DayPicker } from 'react-day-picker';
import 'react-day-picker/style.css';
import { format, isPast, addDays, isToday, isSameDay, parseISO } from 'date-fns';
import { ru } from 'date-fns/locale';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import BottomNav from '../components/BottomNav';
import { X } from 'lucide-react';

export default function BookingPage() {
  const { user, logout } = useAuth();
  const [days, setDays] = useState([]);
  const [times, setTimes] = useState([]);
  const [notWdays, setNotWdays] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [instructorId, setInstructorId] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [selectedDate, setSelectedDate] = useState(undefined);
  const [selectedTime, setSelectedTime] = useState('');
  const [selectedPlace, setSelectedPlace] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [occupiedSlots, setOccupiedSlots] = useState([]);
  const [locations, setLocations] = useState([]);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Лимиты
  const [limits, setLimits] = useState({ weeklyBookingLimit: 3 });
  const [bookingsThisWeek, setBookingsThisWeek] = useState(0);
  const [limitReached, setLimitReached] = useState(false);

  // Статус удаления
  const [deletionStatus, setDeletionStatus] = useState(null);

  const today = new Date();
  const maxDate = addDays(today, 21);

  // ─── Helpers ────────────────────────────────────────────────────────────────

  const countBookingsInWeek = (bookings, targetDate) => {
    if (!bookings || !Array.isArray(bookings)) return 0;
    const date = new Date(targetDate);
    const day = date.getDay();
    const monday = new Date(date);
    monday.setDate(date.getDate() - (day === 0 ? 6 : day - 1));
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const start = format(monday, 'yyyy-MM-dd');
    const end = format(sunday, 'yyyy-MM-dd');
    return bookings.filter(b => b.date >= start && b.date <= end).length;
  };

  const upcomingBookings = bookings
    .filter(b => {
      const bd = parseISO(b.date);
      return !isPast(bd) || isToday(bd);
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const pastBookings = bookings
    .filter(b => {
      const bd = parseISO(b.date);
      return isPast(bd) && !isToday(bd);
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  // ─── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    if (showModal && instructorId) {
      fetch(`/api/instructor-schedule/${instructorId}`)
        .then(r => (r.ok ? r.json() : []))
        .then(setOccupiedSlots)
        .catch(() => setOccupiedSlots([]));
    }
  }, [showModal, instructorId]);

  const loadLimits = async () => {
    try {
      const res = await fetch('/api/limits');
      if (res.ok) {
        const data = await res.json();
        setLimits(data);
      }
    } catch (err) {
      console.error('Ошибка загрузки лимитов:', err);
    }
  };

  useEffect(() => {
    Promise.all([
      fetch('/api/days').then(r => (r.ok ? r.json() : [])).catch(() => []),
      fetch('/api/times').then(r => (r.ok ? r.json() : [])).catch(() => []),
      fetch('/api/locations').then(r => (r.ok ? r.json() : [])).catch(() => []),
      loadLimits(),
    ]).then(([d, t, l]) => {
      setDays(d);
      setTimes(t);
      setLocations(l);
    });

    const interval = setInterval(loadLimits, 30000);

    if (user?.id) {
      fetch(`/api/profile/${user.id}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      })
        .then(r => (r.ok ? r.json() : { booking: [], instructor: null }))
        .then(data => {
          const bookingsData = data.booking || [];
          setBookings(bookingsData);
          setBookingsThisWeek(countBookingsInWeek(bookingsData, new Date()));

          const instrId = data.instructor?.id || null;
          setInstructorId(instrId);

          if (instrId) {
            Promise.all([
              fetch(`/api/not-wdays/${instrId}`).then(r => (r.ok ? r.json() : [])),
              fetch(`/api/instructor-schedule/${instrId}`).then(r => (r.ok ? r.json() : [])),
            ])
              .then(([wd, sched]) => {
                setNotWdays(wd);
                setOccupiedSlots(sched);
              })
              .catch(() => {
                setNotWdays([]);
                setOccupiedSlots([]);
              });
          }
        })
        .catch(() => {
          setBookings([]);
          setInstructorId(null);
          setNotWdays([]);
          setOccupiedSlots([]);
        });
    }

    return () => clearInterval(interval);
  }, [user?.id]);

  useEffect(() => {
    if (limits?.weeklyBookingLimit !== undefined) {
      setLimitReached(bookingsThisWeek >= limits.weeklyBookingLimit);
    }
  }, [bookingsThisWeek, limits]);

  useEffect(() => {
    const checkDeletion = async () => {
      if (!user?.id) return;
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`/api/user/deleted-status/${user.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const data = await res.json();
          setDeletionStatus(data);
        }
      } catch (err) {
        console.error('Ошибка проверки статуса удаления:', err);
      }
    };
    checkDeletion();
  }, [user?.id]);

  // ─── Handlers & computed ────────────────────────────────────────────────────

  const isDisabled = date => {
    if (isPast(date) && !isToday(date)) return true;
    if (date > maxDate) return true;
    const dayOfWeek = format(date, 'EEEE', { locale: ru });
    const dateStr = format(date, 'yyyy-MM-dd');
    if (!days.includes(dayOfWeek)) return true;
    if (notWdays.includes(dateStr)) return true;
    if (isToday(date)) return true;
    return false;
  };

  const getAvailableTimes = () => {
    if (!selectedDate || !instructorId) return [];
    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const now = currentTime;
    const curH = now.getHours();
    const curM = now.getMinutes();

    const occupied = occupiedSlots.filter(s => s.date === dateStr).map(s => s.time);

    return times.filter(time => {
      if (occupied.includes(time)) return false;
      if (isSameDay(selectedDate, today)) {
        const [h, m] = time.split(':').map(Number);
        if (h < curH || (h === curH && m <= curM)) return false;
      }
      return true;
    });
  };

  const availableTimes = getAvailableTimes();

  const handleSubmit = async e => {
    e.preventDefault();

    const selectedWeekCount = countBookingsInWeek(bookings, selectedDate);
    if (selectedWeekCount >= (limits.weeklyBookingLimit || 3)) {
      setMessage('Лимит записей на этой неделе исчерпан.');
      return;
    }

    if (!selectedDate || !selectedTime || !selectedPlace) {
      setMessage('Заполните все поля');
      return;
    }

    setLoading(true);
    setMessage('');

    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    try {
      const res = await fetch(`/api/add-booking/${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
        body: JSON.stringify({ date: dateStr, time: selectedTime, place: selectedPlace }),
      });

      const data = await res.json();

      if (res.ok) {
        setMessage('Запись успешна!');
        const newBooking = { date: dateStr, time: selectedTime, place: selectedPlace };
        const updated = [...bookings, newBooking];
        setBookings(updated);
        setBookingsThisWeek(countBookingsInWeek(updated, new Date()));
        setShowModal(false);
        setSelectedDate(undefined);
        setSelectedTime('');
        setSelectedPlace('');

        if (instructorId) {
          fetch(`/api/instructor-schedule/${instructorId}`)
            .then(r => (r.ok ? r.json() : []))
            .then(setOccupiedSlots);
        }
      } else {
        setMessage(data.message || 'Ошибка при записи');
      }
    } catch {
      setMessage('Ошибка сервера');
    } finally {
      setLoading(false);
    }
  };

  // ─── Render ─────────────────────────────────────────────────────────────────

  if (deletionStatus?.isDeleted) {
    return (
      <div className="min-h-screen bg-[#1b1b1b] text-[#dedede] flex items-center justify-center p-4">
        <div className="bg-red-900/30 border-2 border-red-500 rounded-3xl p-8 max-w-md text-center">
          <div className="text-6xl mb-4">⚠️</div>
          <h2 className="text-2xl font-black mb-4">Аккаунт будет удалён</h2>
          <p className="mb-4">Осталось дней: {deletionStatus.daysLeft}</p>
          <p className="text-sm opacity-75 mb-6">
            Запись на занятия недоступна. Обратитесь к администратору.
          </p>
          <button onClick={logout} className="px-8 py-4 bg-red-600 text-white font-black rounded-full">
            Выйти
          </button>
        </div>
      </div>
    );
  }

  if (user?.status !== 'student') {
    return (
      <div className="min-h-screen bg-[#1b1b1b] flex items-center justify-center text-red-500 text-2xl">
        Доступ запрещён
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1b1b1b] text-[#dedede] font-['Inter',sans-serif] antialiased overflow-x-hidden pb-[80px] lg:pb-0">
      <Header />

      <section className="py-20 lg:py-24 bg-[#1b1b1b]">
        <div className="max-w-screen-2xl mx-auto px-6 sm:px-8">
          <div className="mb-16 text-center">
            <span className="text-[11px] font-black uppercase tracking-[0.5em] text-primary">Запись</span>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-black uppercase tracking-tighter mt-3">
              Бронирование занятия
            </h1>
          </div>

          {/* Главный блок */}
          <div className="glass-panel rounded-3xl p-6 sm:p-10 md:p-12 shadow-[0_0_60px_rgba(184,151,90,0.12)] mb-10">
            <h2 className="text-2xl sm:text-3xl font-black uppercase text-center mb-8">Ваши записи</h2>

            {/* Лимит */}
            <div className="mb-8 p-5 bg-blue-950/30 rounded-2xl border border-blue-800/30">
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                <span className="text-slate-300">Записей на этой неделе:</span>
                <span className="text-2xl font-bold text-primary">
                  {bookingsThisWeek} / {limits.weeklyBookingLimit || 3}
                </span>
              </div>
              <div className="w-full bg-slate-700 h-2.5 rounded-full mt-3 overflow-hidden">
                <div
                  className="bg-primary h-full transition-all duration-500"
                  style={{ width: `${Math.min(100, (bookingsThisWeek / (limits.weeklyBookingLimit || 3)) * 100)}%` }}
                />
              </div>
              {limitReached && (
                <p className="text-sm text-yellow-400 mt-3 text-center">⚠️ Лимит на этой неделе исчерпан</p>
              )}
            </div>

            {/* Предстоящие */}
            <div className="mb-10">
              <h3 className="text-xl font-black uppercase mb-5 flex items-center gap-3">
                <span>⏳ Предстоящие занятия</span>
                <span className="text-sm bg-primary/30 text-primary px-3 py-1 rounded-full">
                  {upcomingBookings.length}
                </span>
              </h3>

              {upcomingBookings.length === 0 ? (
                <p className="text-center text-slate-400 py-8">Нет предстоящих записей</p>
              ) : (
                <div className="space-y-4">
                  {upcomingBookings.map((b, i) => (
                    <div
                      key={i}
                      className="bg-black/40 backdrop-blur-sm p-5 rounded-2xl border-l-4 border-primary"
                    >
                      <div className="flex justify-between items-start gap-4">
                        <div>
                          <p className="font-medium text-lg">{b.date}</p>
                          <p className="text-slate-400 mt-1">{b.time} • {b.place}</p>
                        </div>
                        <span className="text-xs bg-primary/20 text-primary px-3 py-1.5 rounded-full whitespace-nowrap">
                          Предстоит
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={() => setShowModal(true)}
              className="w-full py-5 px-8 bg-primary text-white font-black rounded-full uppercase tracking-widest text-sm hover:brightness-110 transition"
            >
              Записаться на занятие
            </button>

            {limitReached && (
              <p className="text-sm text-yellow-400 text-center mt-4">Можно записаться на следующую неделю</p>
            )}
          </div>

          {/* Прошедшие */}
          {pastBookings.length > 0 && (
            <div className="glass-panel rounded-3xl p-6 sm:p-10 md:p-12 shadow-[0_0_60px_rgba(184,151,90,0.12)] opacity-90">
              <h3 className="text-2xl font-black uppercase mb-6 flex items-center gap-3">
                <span>✅ Прошедшие занятия</span>
                <span className="text-sm bg-slate-700/40 px-3 py-1 rounded-full">
                  {pastBookings.length}
                </span>
              </h3>
              <div className="space-y-4 max-h-80 overflow-y-auto pr-2">
                {pastBookings.map((b, i) => (
                  <div key={i} className="bg-black/30 p-5 rounded-2xl opacity-80 hover:opacity-100 transition">
                    <div className="flex justify-between items-center gap-4">
                      <div>
                        <p className="font-medium">{b.date}</p>
                        <p className="text-slate-400 mt-1">{b.time} • {b.place}</p>
                      </div>
                      <span className="text-xs text-slate-500 whitespace-nowrap">Завершено</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* МОДАЛЬНОЕ ОКНО */}
      {showModal && (
        <div className="fixed inset-0 bg-black/75 z-[9999] flex items-start justify-center overflow-y-auto">
          <div
            className="
              glass-panel rounded-3xl 
              p-5 sm:p-7 md:p-8 
              mt-16 sm:mt-20 md:mt-24 
              mb-24 sm:mb-32 
              w-full max-w-[380px] sm:max-w-md 
              mx-4 sm:mx-6 
              max-h-[88vh] overflow-y-auto 
              border border-white/10 
              relative shadow-2xl shadow-primary/20
              scrollbar-hide
            "
          >
            {/* Заголовок + кнопка закрытия */}
            <div className="relative mb-6 sm:mb-8 pr-14 sm:pr-16">
              <h2 className="text-2xl sm:text-3xl font-black uppercase text-center">
                Выберите дату и время
              </h2>

              <button
                onClick={() => {
                  setShowModal(false);
                  setSelectedDate(undefined);
                  setSelectedTime('');
                  setSelectedPlace('');
                  setMessage('');
                }}
                className="
                  absolute top-0 right-0
                  bg-black/90 border border-red-600/60 
                  text-red-400 rounded-full p-2.5 
                  hover:bg-red-950/70 hover:text-red-300 
                  transition-all z-20
                "
              >
                <X size={20} />
              </button>
            </div>

            {limitReached && (
              <div className="mb-6 p-4 bg-yellow-950/40 border border-yellow-700/50 rounded-2xl text-center">
                <p className="text-yellow-400 font-medium">Лимит на этой неделе исчерпан</p>
                <p className="text-sm text-slate-300 mt-1">Можно записаться на следующие недели</p>
              </div>
            )}

            <div className="bg-black/40 backdrop-blur rounded-2xl border border-white/10 p-3 sm:p-4 mb-6">
              <DayPicker
                mode="single"
                selected={selectedDate}
                onSelect={setSelectedDate}
                disabled={isDisabled}
                locale={ru}
                showOutsideDays
                fromDate={today}
                toDate={maxDate}
                className="w-full mx-auto rdp-responsive"
                modifiers={{
                  disabled: { before: today, after: maxDate },
                }}
                modifiersStyles={{
                  disabled: { color: 'rgba(255,255,255,0.28)' },
                  selected: { backgroundColor: 'var(--primary)', color: 'white' },
                }}
                components={{
                  Caption: ({ displayMonth }) => (
                    <p className="text-center font-bold text-lg mb-4 capitalize">
                      {format(displayMonth, 'LLLL yyyy', { locale: ru })}
                    </p>
                  ),
                }}
                footer={
                  selectedDate ? (
                    <p className="text-center mt-4 text-green-400 font-medium">
                      Выбрано: {format(selectedDate, 'dd MMMM yyyy', { locale: ru })}
                    </p>
                  ) : (
                    <p className="text-center mt-4 text-slate-400">
                      Выберите доступный день
                    </p>
                  )
                }
              />
            </div>

            {selectedDate && (
              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Время</label>
                  <select
                    value={selectedTime}
                    onChange={e => setSelectedTime(e.target.value)}
                    className="w-full px-5 py-4 bg-white/5 border border-white/15 rounded-full text-white focus:border-primary focus:outline-none appearance-none"
                  >
                    <option value="" className="bg-gray-900">Выберите время</option>
                    {availableTimes.length > 0 ? (
                      availableTimes.map(t => (
                        <option key={t} value={t} className="bg-gray-900">{t}</option>
                      ))
                    ) : (
                      <option value="" disabled className="bg-gray-900">
                        {isSameDay(selectedDate, today) ? 'Все слоты сегодня заняты' : 'Нет свободного времени'}
                      </option>
                    )}
                  </select>
                  <p className="text-xs text-slate-500 mt-2">
                    Свободно: {availableTimes.length} слотов
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">Место</label>
                  <select
                    value={selectedPlace}
                    onChange={e => setSelectedPlace(e.target.value)}
                    className="w-full px-5 py-4 bg-white/5 border border-white/15 rounded-full text-white focus:border-primary focus:outline-none appearance-none"
                  >
                    <option value="" className="bg-gray-900">Выберите место</option>
                    {locations.map((loc, i) => (
                      <option key={i} value={loc} className="bg-gray-900">{loc}</option>
                    ))}
                  </select>
                </div>

                {!instructorId && (
                  <p className="text-red-400 text-center text-sm py-3">
                    У вас пока не назначен инструктор. Обратитесь к администратору.
                  </p>
                )}

                <button
                  onClick={handleSubmit}
                  disabled={loading || !instructorId || !selectedTime || !selectedPlace}
                  className="w-full py-5 px-8 bg-primary text-white font-black rounded-full uppercase tracking-wider text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:brightness-110 transition"
                >
                  {loading ? 'Записываем...' : 'Подтвердить запись'}
                </button>
              </div>
            )}

            {message && (
              <p
                className={`text-center mt-6 font-medium ${
                  message.includes('успешн') ? 'text-green-400' : 'text-red-400'
                }`}
              >
                {message}
              </p>
            )}
          </div>
        </div>
      )}

      <BottomNav />
    </div>
  );
}